import React, { useEffect, useRef, useState } from "react";
import { IonPage, IonContent, IonButton, IonItem, IonLabel, IonModal } from "@ionic/react";
import { Helmet } from "react-helmet";

const heavyEmojis = ["🐘", "🏋️", "🚚", "🚂", "🏠", "⛰️", "🚀", "🛳️", "🏆", "🦣", "🦛"];
const lightEmojis = ["🍃", "🦋", "🎈", "🕊️", "🪶", "☁️", "🌻", "🧸", "🪁", "🍂", "🥄"];

const HeavyLightGame: React.FC = () => {
    const [question, setQuestion] = useState("");
    const [options, setOptions] = useState<string[]>([]);
    const [correctAnswer, setCorrectAnswer] = useState<string>("");
    const [message, setMessage] = useState("");
    const [showImage, setShowImage] = useState(false);
    const modal = useRef<HTMLIonModalElement>(null);

    useEffect(() => {
        generateQuestion();
        document.title = "Heavy and Light – LKG Pre-Math Worksheet | Mama’s Guidance"; // Set page title
    }, []);

    const generateQuestion = () => {
        const heavy = heavyEmojis[Math.floor(Math.random() * heavyEmojis.length)];
        const light = lightEmojis[Math.floor(Math.random() * lightEmojis.length)];
        const isHeavyQuestion = Math.random() < 0.5;

        setQuestion(isHeavyQuestion ? "Which one is heavier?" : "Which one is lighter?");
        setOptions(Math.random() < 0.5 ? [heavy, light] : [light, heavy]);
        setCorrectAnswer(isHeavyQuestion ? heavy : light);
        setMessage("");
    };

    const checkAnswer = (selected: string) => {
        if (selected === correctAnswer) {
            setShowImage(true);
            setTimeout(() => {
                generateQuestion()
                setShowImage(false);
                setMessage("")
            }, 5000)
        } else {
            setShowImage(false);
            setMessage("❌ Oops! Try again.");
        }
    };

    return (
        <>
            <Helmet>
                <meta name="description" content='Introduce the concept of heavy and light to LKG students with fun and engaging activities on Mama’s Guidance—designed to develop early comparison and reasoning skills through interactive visual learning.' />
            </Helmet>
            {
                showImage === false ?
                    <>
                        <div className="contentPart additionWord heavyLight matchImage match-container game-container">
                            <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                            <IonModal
                                ref={modal}
                                trigger="open-modal"
                                initialBreakpoint={1}
                                breakpoints={[0, 0.5, 0.75, 1]}
                                handleBehavior="cycle"
                                backdropDismiss={false}  // ✅ Prevents click-to-close
                                className="full-page-modal"
                            >
                                <IonContent className="ion-padding">
                                    <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                    <h1>Heavy and Light</h1>

                                    <p>
                                        Learning to compare weights is an essential part of early childhood math. With this <strong>Heavy and Light</strong> worksheet for LKG students, children can explore the differences in weight between objects using colorful, relatable images and guided questions.
                                    </p>

                                    <h2>⚖️ What Is the Concept of Heavy and Light?</h2>
                                    <p>
                                        “<strong>Heavy</strong>” refers to objects that weigh more, and “<strong>Light</strong>” refers to those that weigh less. Teaching this pre-math concept helps children understand <strong>comparison, balance, and logical thinking</strong> — foundational for future learning.
                                    </p>

                                    <h2>🧩 What’s in the Worksheet?</h2>
                                    <ul>
                                        <li>Picture comparisons of everyday items (like <strong>bags, fruits, boxes</strong>)</li>
                                        <li>Circle the heavy or light object</li>
                                        <li>Match heavy items with light ones</li>
                                        <li>Simple real-world situations (<strong>e.g., weighing scale images</strong>)</li>
                                    </ul>

                                    <h2>🎯 Learning Objectives</h2>
                                    <ul>
                                        <li>Identify heavy and light objects visually</li>
                                        <li>Understand comparison through observation</li>
                                        <li>Build descriptive vocabulary (<strong>heavy, light, heavier, lighter</strong>)</li>
                                        <li>Strengthen early measurement awareness</li>
                                    </ul>

                                    <h2>👩‍🏫 How to Use the Worksheet</h2>
                                    <ul>
                                        <li>Show two pictures and ask, <strong>“Which one is heavy? Which is light?”</strong></li>
                                        <li>Have your child circle or point to the correct object.</li>
                                        <li>Use household items like a toy car and a balloon to demonstrate physically.</li>
                                        <li>Encourage your child to lift objects to feel the difference between weights.</li>
                                    </ul>


                                    <h2>🔗 Related Topics</h2>
                                    <div>
                                        <IonItem routerLink="/lkg/mathematics/pre-math-concepts/long-short"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Long - Short</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/lkg/mathematics/pre-math-concepts/big-small"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Big - Small</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/lkg/mathematics/pre-math-concepts/full-empty"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Full - Empty</IonLabel>
                                        </IonItem>
                                    </div>

                                    <h2>🧠 Why This Concept Is Important</h2>
                                    <p>
                                        By learning about <strong>heavy and light</strong>, children begin to understand <strong>mass, balance, and physical comparison</strong>. It prepares them for real-world decision-making and future science or math learning.
                                    </p>

                                    <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                                </IonContent>
                            </IonModal>
                            <article className="visually-hidden">
                                <h1>Heavy and Light</h1>

                                <p>
                                    Learning to compare weights is an essential part of early childhood math. With this <strong>Heavy and Light</strong> worksheet for LKG students, children can explore the differences in weight between objects using colorful, relatable images and guided questions.
                                </p>

                                <h2>⚖️ What Is the Concept of Heavy and Light?</h2>
                                <p>
                                    “<strong>Heavy</strong>” refers to objects that weigh more, and “<strong>Light</strong>” refers to those that weigh less. Teaching this pre-math concept helps children understand <strong>comparison, balance, and logical thinking</strong> — foundational for future learning.
                                </p>

                                <h2>🧩 What’s in the Worksheet?</h2>
                                <ul>
                                    <li>Picture comparisons of everyday items (like <strong>bags, fruits, boxes</strong>)</li>
                                    <li>Circle the heavy or light object</li>
                                    <li>Match heavy items with light ones</li>
                                    <li>Simple real-world situations (<strong>e.g., weighing scale images</strong>)</li>
                                </ul>

                                <h2>🎯 Learning Objectives</h2>
                                <ul>
                                    <li>Identify heavy and light objects visually</li>
                                    <li>Understand comparison through observation</li>
                                    <li>Build descriptive vocabulary (<strong>heavy, light, heavier, lighter</strong>)</li>
                                    <li>Strengthen early measurement awareness</li>
                                </ul>

                                <h2>👩‍🏫 How to Use the Worksheet</h2>
                                <ul>
                                    <li>Show two pictures and ask, <strong>“Which one is heavy? Which is light?”</strong></li>
                                    <li>Have your child circle or point to the correct object.</li>
                                    <li>Use household items like a toy car and a balloon to demonstrate physically.</li>
                                    <li>Encourage your child to lift objects to feel the difference between weights.</li>
                                </ul>


                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/lkg/mathematics/pre-math-concepts/long-short"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Long - Short</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/lkg/mathematics/pre-math-concepts/big-small"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Big - Small</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/lkg/mathematics/pre-math-concepts/full-empty"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Full - Empty</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>🧠 Why This Concept Is Important</h2>
                                <p>
                                    By learning about <strong>heavy and light</strong>, children begin to understand <strong>mass, balance, and physical comparison</strong>. It prepares them for real-world decision-making and future science or math learning.
                                </p>
                            </article>
                            <h2>{question}</h2>
                            <div className="optionImageDiv" style={{ display: "flex", justifyContent: "center", gap: "30px" }}>
                                {options.map((emoji, index) => (
                                    <div key={index} onClick={() => checkAnswer(emoji)}>
                                        {emoji}
                                    </div>
                                ))}
                            </div>
                            <p style={{ fontSize: "1.5rem" }}>{message}</p>
                        </div>
                    </>
                    :
                    <>
                        <div className="contentPart showMiddle">
                            <img style={{ height: '150px' }} className='img-fluid' src={process.env.REACT_APP_WELDONE_LINK} />
                        </div>
                    </>
            }
        </>
    );
};

export default HeavyLightGame;
